import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, ArrowRight, Info } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import API_URL from '../api';

function Home() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  const card = {
    background: darkMode ? '#1a1a2e' : '#fff', color: darkMode ? '#e0e0e0' : '#333',
    borderRadius: '16px', overflow: 'hidden',
    boxShadow: darkMode ? '0 2px 15px rgba(0,0,0,0.3)' : '0 2px 15px rgba(0,0,0,0.07)',
    border: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f0f0f0',
    transition: 'transform 0.2s, box-shadow 0.2s'
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/events`)
      .then(res => { setEvents(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid #e94560', borderTop: '3px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 15px' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ color: '#666' }}>Loading events...</p>
      </div>
    </div>
  );

  return (
    <div>
      <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', padding: '70px 30px', textAlign: 'center', color: '#fff' }}>
        <div style={{ display: 'inline-block', background: 'rgba(233,69,96,0.15)', border: '1px solid rgba(233,69,96,0.3)', borderRadius: '20px', padding: '6px 16px', fontSize: '13px', color: '#e94560', marginBottom: '20px' }}>🎓 College Event Management System</div>
        <h1 style={{ fontSize: '46px', fontWeight: '800', color: '#fff', marginBottom: '15px', lineHeight: '1.2' }}>Discover & Join<br /><span style={{ color: '#e94560' }}>College Events</span></h1>
        <p style={{ color: '#aaa', fontSize: '17px', maxWidth: '500px', margin: '0 auto 30px' }}>Register for events instantly and get your QR ticket delivered to your email!</p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <div style={{ background: 'rgba(233,69,96,0.15)', border: '1px solid rgba(233,69,96,0.3)', borderRadius: '20px', padding: '8px 20px', color: '#e94560', fontSize: '14px' }}>🎟️ {events.length} Events Available</div>
          <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '20px', padding: '8px 20px', color: '#fff', fontSize: '14px' }}>📱 Instant QR Tickets</div>
          <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '20px', padding: '8px 20px', color: '#fff', fontSize: '14px' }}>📧 Email Confirmation</div>
        </div>
      </div>

      <div className="container" style={{ marginTop: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
          <div>
            <h2 style={{ color: darkMode ? '#fff' : '#1a1a2e' }}>Upcoming Events</h2>
            <p style={{ color: '#888', marginTop: '4px', fontSize: '14px' }}>Register now to secure your spot</p>
          </div>
          <span style={{ color: '#888', fontSize: '14px', background: darkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', padding: '6px 14px', borderRadius: '20px' }}>{events.length} events</span>
        </div>

        {events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', ...card }}>
            <div style={{ fontSize: '50px', marginBottom: '15px' }}>📭</div>
            <h3 style={{ color: '#999', fontWeight: '400' }}>No events yet</h3>
            <p style={{ color: '#bbb', marginTop: '5px' }}>Check back later!</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '25px' }}>
            {events.map((event, i) => {
              const seatsLeft = event.totalSeats - event.registeredCount;
              const isFull = seatsLeft <= 0;
              const isAlmostFull = seatsLeft <= 5 && seatsLeft > 0;
              const isLocked = event.isLocked;
              const isDeadlinePassed = event.registrationDeadline && new Date() > new Date(event.registrationDeadline);
              const canRegister = !isFull && !isLocked && !isDeadlinePassed;

              return (
                <div key={event._id} style={{ ...card, animation: `fadeInUp 0.4s ease ${i * 0.08}s both` }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 35px rgba(0,0,0,0.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = darkMode ? '0 2px 15px rgba(0,0,0,0.3)' : '0 2px 15px rgba(0,0,0,0.07)'; }}>

                  {/* Card Header */}
                  <div style={{ background: 'linear-gradient(135deg, #1a1a2e, #0f3460)', padding: '22px', position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ background: 'rgba(233,69,96,0.2)', borderRadius: '8px', padding: '6px 10px', display: 'inline-block', marginBottom: '10px' }}>
                          <span style={{ color: '#e94560', fontSize: '12px', fontWeight: '600' }}>🎪 EVENT</span>
                        </div>
                        <h2 style={{ color: '#fff', fontSize: '18px', lineHeight: '1.3' }}>{event.title}</h2>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'flex-end' }}>
                        {isFull && <span style={{ background: '#ff4444', color: '#fff', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>FULL</span>}
                        {isAlmostFull && !isFull && <span style={{ background: '#ff9800', color: '#fff', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>ALMOST FULL</span>}
                        {isLocked && <span style={{ background: '#ff4444', color: '#fff', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>🔒 LOCKED</span>}
                        {isDeadlinePassed && !isLocked && <span style={{ background: '#ff9800', color: '#fff', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>DEADLINE PASSED</span>}
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div style={{ padding: '20px' }}>
                    <p style={{ color: darkMode ? '#aaa' : '#666', fontSize: '14px', marginBottom: '15px', lineHeight: '1.5' }}>{event.description}</p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: darkMode ? '#aaa' : '#555' }}>
                        <Calendar size={15} color="#e94560" />{new Date(event.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: darkMode ? '#aaa' : '#555' }}>
                        <MapPin size={15} color="#e94560" />{event.venue}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                        <Users size={15} color="#e94560" />
                        <span style={{ color: isFull ? '#ff4444' : isAlmostFull ? '#ff9800' : '#2e7d32', fontWeight: '600' }}>
                          {isFull ? 'No seats available' : `${seatsLeft} seats left`}
                        </span>
                      </div>
                      {event.registrationDeadline && !isDeadlinePassed && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#ff9800' }}>
                          ⏰ Deadline: {new Date(event.registrationDeadline).toLocaleDateString('en-GB')}
                        </div>
                      )}
                    </div>

                    {/* Progress Bar */}
                    <div style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: '10px', height: '6px', marginBottom: '18px' }}>
                      <div style={{ background: isFull ? '#ff4444' : isAlmostFull ? '#ff9800' : 'linear-gradient(135deg, #e94560, #c62a47)', borderRadius: '10px', height: '6px', width: `${Math.min((event.registeredCount / event.totalSeats) * 100, 100)}%`, transition: '0.3s' }} />
                    </div>

                    {/* Buttons */}
                    <button
                      onClick={() => navigate(`/event/${event._id}`)}
                      style={{ background: darkMode ? 'rgba(255,255,255,0.07)' : '#f5f5f5', color: darkMode ? '#ccc' : '#555', width: '100%', padding: '11px', borderRadius: '10px', marginBottom: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #eee', cursor: 'pointer' }}>
                      <Info size={15} /> View Details
                    </button>

                    <button
                      onClick={() => canRegister && navigate(`/register/${event._id}`)}
                      disabled={!canRegister}
                      style={{ background: canRegister ? 'linear-gradient(135deg, #e94560, #c62a47)' : '#ccc', color: '#fff', width: '100%', padding: '12px', fontSize: '15px', borderRadius: '10px', cursor: canRegister ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      {!canRegister ? (isLocked ? '🔒 Locked' : isDeadlinePassed ? '⏰ Deadline Passed' : 'Event Full') : (<>Register Now <ArrowRight size={16} /></>)}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Home;